import type {
  BandLayoutPose,
  BandLayoutPreset,
  CrossChannelManualField,
  MetalParamName,
  RingBuilderClientApi,
  RingState,
} from "../../shared/api-contract";
import type { Caps } from "../editor/caps";
import type { RingRow } from "./RingManagerModal";
import { migrateRingState } from "./stateMigrate";
import { applyRecipeLook, isRecipeLookId, type RecipeLookId } from "./recipes/looks";

/**
 * What the viewer on the other end can take, read off its contract version
 * (`caps.ts`). RPC has no timeout, so a setter an older viewer lacks would
 * hang the replay; and one viewer generation (pre-v66) carves a groove it
 * does not know as something else entirely.
 */
export interface RingReplayCaps {
  /** v41 `setDesignGrooves`: the list is replayed whenever it can be — an EMPTY one too, or undo could never remove the first groove. */
  designGrooves: boolean;
  /** v55 `setCrossChannel` / `setCrossChannelManual`: the trench, its owned fields and its cut shape. */
  crossChannel: boolean;
  /** v66: the viewer carves "path" grooves. An older one cuts each as a full-circumference groove on the centre line, so they are left out. */
  pathGrooves: boolean;
  /** v67 `loadRingState`: the viewer restores a whole RingState itself, in ONE rebuild, instead of ~40 setter round trips and as many rebuilds. */
  loadState: boolean;
}

export function replayCaps(caps: Pick<Caps, "ringLongTailV41" | "ringCrossChannelV55" | "ringPathGroovesV66" | "ringDesignV67">): RingReplayCaps {
  return {
    designGrooves: caps.ringLongTailV41,
    crossChannel: caps.ringCrossChannelV55,
    pathGrooves: caps.ringPathGroovesV66,
    loadState: caps.ringDesignV67,
  };
}

/**
 * A ring's state with its ring-manager labels (`ringType` / `ringName`) left
 * out — for copying one ring's DESIGN onto another (the linked converge on
 * add, Match). v67 `loadRingState` sets the labels a state carries, which is
 * right for undo and a shared ring and wrong for a copy across rings.
 */
export function withoutRingLabels(s: RingState): RingState {
  const { ringType: _type, ringName: _name, ...rest } = s;
  return rest as RingState;
}

/**
 * A ring's DESIGN for copying onto ANOTHER ring (the linked converge on add,
 * Match): its labels AND the wearer's own fields left out — ring size, the
 * inscription, the spec — and its logo marked "keep yours" (`logo: undefined`
 * with the key present; a v67 restore clears an ABSENT logo key). The logo is
 * personal like the text, and the live link never mirrors it either; copying
 * it here put one ring's mark inside the other (review finding). The
 * engagement head stays: whether it travels is the caller's existing rule.
 */
export function withoutWearer(s: RingState): RingState {
  const { ringType: _t, ringName: _n, ringSizeUS: _us, ringSizeEU: _eu, engravingText: _txt, spec: _spec, ...rest } = s as RingState & { spec?: unknown };
  const out = rest as RingState;
  if (out.engraving) out.engraving = { ...out.engraving, logo: undefined };
  return out;
}

/** What a replay could not put back, in sentences for the user (empty when everything went in). */
export interface RingReplayResult {
  left: string[];
}

/** Every `CrossChannelParams` field a user can own (`setCrossChannelManual`). */
const CROSS_CHANNEL_FIELDS: readonly CrossChannelManualField[] = ["widthMM", "depthMM", "centerAngleDeg", "spanMM", "positionMM", "diagonalDeg"];

/**
 * Restore a whole `RingState` into the viewer: undo/redo, `?design=`, a share
 * link, the linked-ring converge.
 *
 * v67+: ONE call, `loadRingState` — the viewer runs the same setters in this
 * same order inside one batch (one rebuild, one `ringStateChanged`), with the
 * wearer fields (ring size, engraving text, the head) applied to the active
 * ring only. Older viewers get the field-by-field replay below.
 *
 * Before v67 there was no "load a RingState" RPC on the merged builder: `loadEngagementDesign`
 * only feeds the standalone engagement configurator (`engCfg()`), which is null
 * on `/band.html` — the merged path keeps the engagement block on the *band*
 * configurator, and `BandConfigurator` has no `load()`. So restoring a shared
 * `?design=` token, or stepping undo/redo, means driving the setters.
 *
 * Draft-batched, so the ~25 round trips produce one high-resolution rebuild.
 *
 * The replay below is that pre-v67 path, kept verbatim for older viewers; v67
 * collapsed it into the single `loadRingState` RPC above (`BandConfigurator.
 * loadRingState` runs these setters in this order).
 */
export async function applyRingState(r: RingBuilderClientApi, sIn: RingState, caps: RingReplayCaps): Promise<RingReplayResult> {
  // Rewrite retired ids BEFORE any setter runs. An unknown DiamondStyle
  // resolves to `none` in the engine and the ring silently loses its stones;
  // an unknown finishId degrades to polished. Both are graceful at runtime and
  // invisible to the user, which is exactly why the migration has to happen
  // here rather than being left to the fallbacks.
  const { state: s, notes } = migrateRingState(sIn);
  if (notes.length && import.meta.env?.DEV) {
    console.info(
      `[ringState] migrated ${notes.length} retired id(s):`,
      notes.map((x) => `${x.path} ${x.from} -> ${x.to}`).join(", "),
    );
  }
  if (caps.loadState) {
    await r.loadRingState(s);
    return { left: [] };
  }
  const left: string[] = [];
  await r.beginDraft();
  try {
    // A "custom" profile is a .3dm point list that never crossed postMessage, so
    // it cannot be rebuilt from a snapshot — keep whatever profile is loaded.
    // v67 parts an older viewer cannot build are left out, and SAID (a v67
    // design opened against an embedded older viewer, or in a deploy window).
    if (s.profileId === "param") left.push("The custom cross-section was left out: this viewer is too old for it, so the ring keeps its current profile.");
    else if (s.profileId && s.profileId !== "custom") await r.setProfile(s.profileId);
    await r.setRingSize(s.ringSizeUS);
    await r.setWidth(s.widthMM);
    await r.setThickness(s.thicknessMM);
    await r.setMetalKarat(s.metalKarat);
    await r.setAngularSegments(s.angularSegments);
    await r.setBandStyle(s.bandStyle);
    await r.setTwist(s.twist);
    await r.setSplitMode(s.splitMode);
    await r.setSplitPattern(s.splitPatternId);

    for (let i = 0; i < s.materials.length; i++) {
      const m = s.materials[i];
      await r.setPartitionMaterial(i, m.presetId, m.finishId);
      // v41: only a name the user actually typed. The viewer fills the
      // positional default on read, so replaying it verbatim would pin a label
      // that should follow the partition index.
      if (m.label && m.label !== `Metal ${i + 1}`) await r.setPartitionLabel(i, m.label);
      if (!m.override) continue;
      const { color, ...params } = m.override;
      if (color) await r.setPartitionMetalColor(i, color);
      for (const [name, value] of Object.entries(params)) {
        if (typeof value === "number") await r.setPartitionMetalParam(i, name as MetalParamName, value);
      }
    }

    await r.setEdgeStyle(s.edgeStyle);
    await r.setGrooves(s.grooves);
    if (s.diamond?.style === "pattern" || s.stonePattern) left.push("The stone pattern was left out: this viewer is too old to build it.");
    await r.setDiamond(s.diamond);
    await r.setChannel(s.channel);
    // v55 — AFTER setDiamond, which re-derives every trench field nobody owns:
    // the cut shape (never owned), then each owned field at its saved value and
    // every other one handed back to the derivation (null). Without this, undo
    // and `?design=` brought a diagonal stone row back straight.
    if (caps.crossChannel && s.crossChannel) {
      const cc = s.crossChannel;
      await r.setCrossChannel({ cutShape: cc.cutShape });
      await r.setCrossChannelManual(Object.fromEntries(CROSS_CHANNEL_FIELDS.map((k) => [k, cc.manual?.includes(k) ? cc[k] : null])));
    }
    await r.setEngravingText(s.engravingText);
    if (s.engraving?.logo) {
      // An older viewer has no logo sanitiser: never hand it one.
      left.push("The logo inside the band was left out: this viewer is too old for it.");
      const { logo: _logo, ...engraving } = s.engraving;
      await r.setEngraving(engraving as RingState["engraving"]);
    } else {
      await r.setEngraving(s.engraving);
    }

    // v37 fields — only sent when the snapshot actually carries them, so a
    // pre-v37 design never names a method an older viewer would reject.
    if (s.shape) await r.setRingShape(s.shape);
    if (s.division) await r.setDivision(s.division);
    if (s.comfortFitMM !== undefined) await r.setComfortFit(s.comfortFitMM);
    if (s.grooves.jointType || s.grooves.seamBoundaries) await r.setJointGrooves(s.grooves);

    // v38 fields — same rule as v37: only sent when the snapshot carries them,
    // so a design made before this wave never names a method an older viewer
    // would reject. `setAutoThickness` runs after `setThickness` on purpose;
    // it recomputes the same value the snapshot already holds.
    if (s.edgeSides) await r.setEdgeSides(s.edgeSides);
    if (s.insidePolished) await r.setInsidePolished(true);
    if (s.autoThickness) await r.setAutoThickness(true);

    // v39/v40 — same rule again: only sent when the snapshot carries them, so a
    // design made before those waves never names a method an older viewer
    // rejects. Without these, undo/redo and `?design=` silently dropped the
    // inner stone and the whole free arrangement.
    if (s.innerDiamond) await r.setInnerDiamond(s.innerDiamond);
    if (s.freeStones) await r.setFreeStones(s.freeStones);
    if (s.freeRepeat !== undefined) await r.setFreeRepeat(s.freeRepeat);
    if (s.freeRotateDeg !== undefined) await r.setFreeRotate(s.freeRotateDeg);

    // v41 — replayed whenever the viewer has the setter, EMPTY included: a
    // fresh ring's snapshot has no list at all, and skipping it meant undoing
    // the first groove (or a whole bypass shoulder) left it carved and cut.
    // A pre-v66 viewer gets no "path" groove (see `RingReplayCaps`).
    if (caps.designGrooves) {
      const grooves = s.designGrooves ?? [];
      const kept = caps.pathGrooves ? grooves : grooves.filter((g) => g.direction !== "path");
      if (kept.length < grooves.length) {
        const n = grooves.length - kept.length;
        left.push(`${n} stepped-shoulder / path groove${n === 1 ? " was" : "s were"} left out: this viewer is too old to cut ${n === 1 ? "it" : "them"} correctly.`);
      }
      if (kept.some((g) => (g as { vAngleDeg?: number }).vAngleDeg !== undefined)) {
        left.push("A groove's exact V angle was left out: this viewer is too old for it, so it cuts its named V.");
      }
      await r.setDesignGrooves(kept);
    } else if (s.designGrooves?.length) {
      left.push("The design grooves were left out: this viewer is too old for them.");
    }
    // `setCustomRatios` runs after `setSplitPattern` above so the ratios are
    // re-fitted to the count the pattern just established.
    // v51 — same rule as every optional field above: only sent when the snapshot
    // carries it, so a design made before this wave never names a method an
    // older viewer lacks. Without this line the bead settings would vanish from
    // every `?design=` link and from undo/redo while still working live.
    if (s.milgrain) await r.setMilgrain(s.milgrain);
    if (s.customRatios?.length) await r.setCustomRatios(s.customRatios);

    if (s.engagement) {
      await r.setEngagementEnabled(s.engagement.enabled);
      if (s.engagement.enabled) await r.setEngagement(s.engagement);
    }
  } finally {
    await r.endDraft();
  }
  return { left };
}

/**
 * The saved document is the whole SET, not the ring being edited (v43).
 *
 * Save/share used to serialize `serializeEngagementDesign()`, which the bridge
 * builds from the ACTIVE configurator only — so sharing a linked pair and opening
 * the link gave you one ring, with the arrangement and the link gone. `rings[]`
 * carries every ring, plus the two set-level facts no ring state holds.
 */
export interface RingSetDoc {
  rings: RingState[];
  layout?: BandLayoutPreset;
  linked?: boolean;
  /** v68: the photographed pose over `layout` (`setBandLayout(layout, pose)`, caps `ringPoseV68`). Absent ⇒ the preset's law. */
  pose?: BandLayoutPose;
  /** v68: the named look (`recipes/looks.ts`, caps `ringLookV68`). Absent ⇒ "studio". */
  look?: RecipeLookId;
  /** v68: the size the last recipe gave ring 1 — while ring 1 still has it, the next recipe may resize it (`recipeSetDoc`). */
  recipePartnerSizeUS?: number;
}

/** Reads both shapes: a set document, and every pre-v43 single-ring design that
 * is already out there in a `?design=` link. */
export function parseRingSetDoc(json: string): RingSetDoc {
  const raw = JSON.parse(json) as RingSetDoc | RingState;
  const doc = raw as RingSetDoc;
  return Array.isArray(doc.rings) ? doc : { rings: [raw as RingState] };
}

/**
 * Serialize every ring in the set. Walks the ring list with `setActiveRing`,
 * which is read-only — no setter runs, so nothing is mirrored to the other rings
 * even while the set is linked — and puts the active ring back at the end.
 *
 * The row's name and type are the HOST's (the manager renames without an RPC), so
 * they are stamped onto each ring state here rather than read back from it.
 */
export async function serializeRingSet(
  r: RingBuilderClientApi,
  set: { rows: RingRow[]; active: number; layout: BandLayoutPreset; linked: boolean; pose?: BandLayoutPose | null; look?: RecipeLookId; recipePartnerSizeUS?: number },
): Promise<RingSetDoc> {
  const rings: RingState[] = [];
  try {
    for (let i = 0; i < set.rows.length; i++) {
      // EVERY ring is selected before it is read. The old `if (i !== set.active)`
      // skipped the active ring's select — but by then the walk had already
      // moved the engine to ring 0, so with ring 1 active the document carried
      // ring 0 twice and ring 1's design was lost (W1 live finding 3).
      if (set.rows.length > 1) await r.setActiveRing(i);
      const s = await r.serializeEngagementDesign();
      if (!s) continue;
      rings.push({ ...s, ringType: set.rows[i].type, ringName: set.rows[i].name || undefined });
    }
  } finally {
    // Even on a mid-walk failure: leaving the engine editing a ring the panel is
    // not showing would send the next slider to the wrong band.
    if (set.rows.length > 1) await r.setActiveRing(set.active);
  }
  if (!rings.length) throw new Error("no design state");
  return withSetExtras({ rings, layout: set.layout, linked: set.linked }, set);
}

/** v68: the set-level extras, only when present (an older doc stays byte-identical). */
function withSetExtras(doc: RingSetDoc, x: { pose?: BandLayoutPose | null; look?: RecipeLookId; recipePartnerSizeUS?: number }): RingSetDoc {
  if (x.pose) doc.pose = x.pose;
  if (x.look && x.look !== "studio") doc.look = x.look;
  if (x.recipePartnerSizeUS !== undefined) doc.recipePartnerSizeUS = x.recipePartnerSizeUS;
  return doc;
}

/**
 * Replay a whole set: ring 0 into the design that already exists, then one
 * `addRing` per further ring (which leaves the new ring active, so the replay
 * lands on it). The link goes on LAST — switched on first, every per-ring replay
 * would be mirrored onto all the others and the set would converge to one design.
 *
 * `caps` keeps an old viewer out of the methods it does not have — RPC has no
 * timeout, so naming `addRing` (v41) or `setRingsLinked` (v42) on one would hang
 * the boot behind the loading overlay forever. Without them the extra rings are
 * simply dropped, which is what a pre-v41 viewer can express.
 */
export async function restoreRingSet(
  r: RingBuilderClientApi,
  doc: RingSetDoc,
  caps: { multiRing: boolean; link: boolean; replay: RingReplayCaps; pose?: boolean; look?: boolean },
): Promise<RingSetDoc & RingReplayResult> {
  if (!doc.rings.length) return { ...doc, left: [] };
  const rings = caps.multiRing ? doc.rings : doc.rings.slice(0, 1);
  const left = new Set<string>();
  await r.setActiveRing(0);
  for (const note of (await applyRingState(r, rings[0], caps.replay)).left) left.add(note);
  for (let i = 1; i < rings.length; i++) {
    // v67: the viewer seeds the new ring through `loadRingState` itself, so the
    // whole ring is one call and one rebuild.
    if (caps.replay.loadState) {
      await r.addRing(migrateRingState(rings[i]).state);
      continue;
    }
    await r.addRing({ ringType: rings[i].ringType, ringName: rings[i].ringName });
    for (const note of (await applyRingState(r, rings[i], caps.replay)).left) left.add(note);
  }
  // "catalogue" frames even a lone ring (its camera), so it is restored for one
  // ring too — on a v67 viewer, the only one that has it.
  // v68: the photographed pose rides `setBandLayout`'s second argument, on a
  // viewer that has it (`getBandLayout` is the new NAME `ringPoseV68` gates);
  // otherwise the preset alone, and the note.
  const pose = doc.pose && caps.pose ? doc.pose : undefined;
  if (doc.pose && !caps.pose) left.add("The photographed pose was left out: this viewer is too old for it.");
  if (doc.layout === "catalogue" && !caps.replay.loadState) left.add("The catalogue pose was left out: this viewer is too old for it.");
  else if (doc.layout && (rings.length > 1 || doc.layout === "catalogue")) {
    if (pose) await r.setBandLayout(doc.layout, pose);
    else await r.setBandLayout(doc.layout);
  }
  const linked = !!doc.linked && caps.link && rings.length > 1;
  if (linked) await r.setRingsLinked(true);
  // v68: the look last (absent ⇒ "studio", a no-op on a viewer still in the studio).
  const look = isRecipeLookId(doc.look) ? doc.look : "studio";
  if (caps.look) await applyRecipeLook(r, look);
  else if (look !== "studio") left.add("The catalogue look was left out: this viewer is too old for it.");
  await r.setActiveRing(0);
  const out: RingSetDoc & RingReplayResult = { rings, layout: doc.layout, linked, left: [...left] };
  return withSetExtras(out, { pose, look: caps.look ? look : undefined, recipePartnerSizeUS: doc.recipePartnerSizeUS }) as RingSetDoc & RingReplayResult;
}

/**
 * Make the viewer's ring set EXACTLY `doc`, whatever set it holds now — for a
 * recipe ("Start from a design") and for undoing / redoing one. The link is
 * switched off first (a restore under a live link would mirror every ring onto
 * the others), every ring past the first is removed, and `restoreRingSet` then
 * rebuilds the set from ring 0 (v67: one `loadRingState` per ring).
 *
 * v67 only: `removeRing` is v41 and the whole-state restore is v67, and both
 * callers are gated on `caps.ringDesignV67`.
 */
export async function replaceRingSet(
  r: RingBuilderClientApi,
  doc: RingSetDoc,
  now: { count: number; linked: boolean },
  caps: { multiRing: boolean; link: boolean; replay: RingReplayCaps; pose?: boolean; look?: boolean },
): Promise<RingSetDoc & RingReplayResult> {
  if (now.linked && caps.link) await r.setRingsLinked(false);
  await r.setActiveRing(0);
  for (let i = now.count - 1; i >= 1; i--) await r.removeRing(i);
  return restoreRingSet(r, doc, caps);
}

/** The whole ring SET at one point in the history: what a recipe replaced, or what it made. */
export interface SetSnapshot {
  doc: RingSetDoc;
  rows: RingRow[];
  active: number;
}

/**
 * One undo step. Most are one ring's state (replayed onto the ring being
 * edited); a step that changed the SET — "Start from a design" — carries the
 * whole set on both sides, so undo/redo across it restores the ring list, the
 * arrangement and the ring being edited exactly (`replaceRingSet`).
 */
export interface HistoryEntry {
  state: RingState;
  set?: SetSnapshot;
}

/**
 * The history after a SET change: the entry at `cursor` (the state just before)
 * gains the whole `before` set — or a new entry is pushed when the latest edit
 * had not been recorded yet — then ONE entry for the new set. Pure, so the
 * check pins "a recipe is one undo step".
 */
export function pushSetStep(
  history: readonly HistoryEntry[],
  cursor: number,
  current: RingState,
  before: SetSnapshot,
  after: { state: RingState; set: SetSnapshot },
  max = 40,
): { history: HistoryEntry[]; cursor: number } {
  const kept = history.slice(0, cursor + 1);
  const last = kept[kept.length - 1];
  if (last && JSON.stringify(last.state) === JSON.stringify(current)) kept[kept.length - 1] = { state: last.state, set: before };
  else kept.push({ state: current, set: before });
  kept.push(after);
  const out = kept.slice(-max);
  return { history: out, cursor: out.length - 1 };
}
